/**
 * @module
 * @format
 * @todo
 *
 *   - Invalidate or update cache when language changes.
 *   - Strip custom markup from {@link WeblogData.content} in the ACNG backend.
 */

/**
 * @import {WeblogData} from './types';
 * @import {Movie} from '../moviePool/model/movie.js';
 * @import {PictureSet} from '../picturePool/model/picture-set.js';
 */

import {service} from 'nova';
import {clear, MapStore} from '@acng/frontend-bounty/collection.js';
import {all} from '@acng/frontend-bounty/std/control.js';
import {guard, INTEGER} from '@acng/frontend-rubicon';

import {cdn} from '../core/service/cdn.js';
import {inject} from '../core/service/ng.js';
import {user} from '../user/user.js';
import {get as getMovie} from '../moviePool/service/cache.js';
import {asWeblogData, WEBLOG_MEDIA_TYPE_MOVIE, WEBLOG_MEDIA_TYPE_PICTURE} from './model.js';
import {Weblog} from './item.js';
import {get as getAmateur} from '../amateurPool/service/cache.js';

@service
class WeblogStore {
  /**
   * @param {"de" | "en"} lang
   * @param {Weblog["id"]} id
   * @returns {Promise<WeblogData>}
   */
  async #fetchData(lang, id) {
    const url = `/api/amateur-profile/weblog/${id}?lang=${lang}`;
    const data = await cdn(url);
    ASSERT: guard(data, asWeblogData);

    data.content = data.content.replace(/\[link\](.*)\[\/link\]/, (_m, a) => a);

    return data;
  }

  /**
   * @param {WeblogData} data
   * @returns {Promise<Movie | PictureSet> | undefined}
   */
  #getOptionalGallery(data) {
    const {media_type, media_id, set_id} = data;

    if (media_type == WEBLOG_MEDIA_TYPE_MOVIE) {
      ASSERT: guard(media_id, INTEGER);
      return getMovie(media_id);
    }

    if (media_type == WEBLOG_MEDIA_TYPE_PICTURE) {
      ASSERT: guard(set_id, INTEGER);
      return inject('PictureSet').get(set_id);
    }
  }

  get = MapStore(
    /**
     * @param {Weblog["id"]} id
     * @param {Map<Weblog["id"], Promise<Weblog>>} cache
     * @returns {Promise<Weblog>}
     */
    async (id, cache) => {
      if (!id) {
        clear(cache);
        queueMicrotask(() => clear(cache));

        throw cache;
      }

      const data = await this.#fetchData(user.lang, id);

      const [creator, gallery] = await all([getAmateur(`${data.user_id}`), this.#getOptionalGallery(data)]);

      return new Weblog(data, creator, gallery);
    },
  );
}

export {WeblogStore};
